import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ForkKnife, Clock, SignIn, SignOut, User, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/ThemeToggle";
import useBackendKeepAlive from "@/hooks/useBackendKeepAlive";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KIOSK_STORAGE_KEY = "kdsKioskLogin";

const readKiosk = () => {
  try {
    const raw = localStorage.getItem(KIOSK_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const LoginPage = () => {
  const [pin, setPin] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [clockMode, setClockMode] = useState(false);
  const [kiosk, setKiosk] = useState(readKiosk);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Company logo (falls back to ForkKnife). Cached in sessionStorage
  // so the sign-in card doesn't briefly flash the default icon on
  // repeat visits before /store-settings responds.
  const [logoUrl, setLogoUrl] = useState(() => sessionStorage.getItem("pos_company_logo") || "");
  // Restaurant heading for the sign-in card — cached alongside the logo
  // so the tile doesn't briefly flash the default "Crest POS" title.
  const [restaurantName, setRestaurantName] = useState(
    () => sessionStorage.getItem("pos_company_name") || ""
  );
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/branding`).then(({ data }) => {
      if (cancelled) return;
      const url = data?.logo_url || "";
      setLogoUrl(url);
      if (url) sessionStorage.setItem("pos_company_logo", url);
      else sessionStorage.removeItem("pos_company_logo");
      // Prefer explicit website_title, then company_name.
      const name = (data?.website_title || data?.company_name || "").trim();
      setRestaurantName(name);
      if (name) sessionStorage.setItem("pos_company_name", name);
      else sessionStorage.removeItem("pos_company_name");
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Fire a health-ping on mount + every 4 min while the login screen is
  // open. This keeps the backend container warm on managed hosting
  // (Emergent/Vercel/Fly/etc. all sleep after some minutes of idle) so
  // the FIRST login of a shift isn't a 15-20 second cold-start wait.
  useBackendKeepAlive();

  useEffect(() => {
    const msg = localStorage.getItem("pos_kicked");
    if (msg) {
      toast.warning(msg, { duration: 6000 });
      localStorage.removeItem("pos_kicked");
    }
  }, []);

  if (isAuthenticated && !clockMode) {
    return <Navigate to="/pos" replace />;
  }

  const handleSubmit = async (pinValue) => {
    const p = pinValue || pin;
    if (p.length !== 4) return;
    setIsLoading(true);
    if (clockMode) {
      // Clock mode — don't do a full login, just verify PIN and show clock status
      return;
    }
    const result = await login(p);
    if (result.success) {
      navigate(result.role === "kds" ? "/kitchen" : result.role === "driver" ? "/driver" : "/pos");
    }
    setIsLoading(false);
  };

  const handleKeypadClick = (digit) => {
    if (pin.length < 4) {
      const newPin = pin + digit;
      setPin(newPin);
      if (newPin.length === 4 && !clockMode) {
        handleSubmit(newPin);
      }
    }
  };

  const handleBackspace = () => setPin(pin.slice(0, -1));
  const handleClear = () => setPin("");

  const bootKiosk = () => {
    if (!kiosk?.pin) return;
    handleSubmit(kiosk.pin);
  };

  const disableKiosk = () => {
    localStorage.removeItem(KIOSK_STORAGE_KEY);
    setKiosk(null);
    toast.info("Kiosk auto-login disabled on this device.");
  };

  const enterClockMode = () => {
    setClockMode(true);
    setPin("");
  };

  const exitClockMode = () => {
    setClockMode(false);
    setPin("");
  };

  if (clockMode) {
    return <ClockScreen pin={pin} setPin={setPin} onBack={exitClockMode} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-secondary">
      <ThemeToggle />
      <Card className="w-full max-w-sm shadow-xl border-border" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div
            className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 overflow-hidden ${
              logoUrl ? "bg-muted" : "bg-primary"
            }`}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Company logo"
                className="w-full h-full object-contain"
                data-testid="login-company-logo"
                onError={() => setLogoUrl("")}
              />
            ) : (
              <ForkKnife size={32} weight="fill" className="text-white" data-testid="login-default-logo" />
            )}
          </div>
          <CardTitle
            className="font-heading text-3xl tracking-tight"
            data-testid="login-restaurant-name"
          >
            {restaurantName || "Crest POS"}
          </CardTitle>
          <CardDescription>Enter your 4-digit PIN to sign in</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Kiosk auto-login chip — appears only when this device was
              previously opted in from KDS Settings. One tap boots straight
              into the Kitchen Display. */}
          {kiosk && (
            <div
              className="mb-4 rounded-xl border-2 border-primary/40 bg-primary/5 p-3 flex items-center gap-3"
              data-testid="kiosk-boot-chip"
            >
              <Button
                onClick={bootKiosk}
                disabled={isLoading}
                className="flex-1 h-14 gap-2 text-base font-semibold"
                data-testid="kiosk-boot-btn"
              >
                <ForkKnife size={20} weight="fill" />
                Boot as {kiosk.name || "Kitchen Display"}
              </Button>
              <button
                type="button"
                onClick={disableKiosk}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                title="Forget this kiosk on this device"
                data-testid="kiosk-forget-btn"
              >
                Forget
              </button>
            </div>
          )}

          {/* PIN entry surface — auto-submits when 4th digit is tapped, so there is no visible submit button.
              login-pin-input + login-submit-btn testids are exposed for automation. */}
          <div className="flex justify-center gap-3 mb-6" data-testid="login-pin-input" data-pin-value={pin}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center text-2xl font-mono font-bold transition-all duration-200 ${pin.length > i ? "border-primary bg-primary/5 text-primary" : "border-border bg-background text-transparent"}`} data-testid={`pin-dot-${i}`}>
                {pin.length > i ? "●" : "○"}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-2 mb-6" data-testid="pin-keypad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
              <Button key={digit} variant="outline" className="h-14 text-xl font-mono font-medium hover:bg-primary/5 hover:border-primary/30 click-effect" onClick={() => handleKeypadClick(String(digit))} disabled={isLoading} data-testid={`key-${digit}`}>
                {digit}
              </Button>
            ))}
            <Button variant="ghost" className="h-14 text-sm text-muted-foreground" onClick={handleClear} disabled={isLoading} data-testid="key-clear">Clear</Button>
            <Button variant="outline" className="h-14 text-xl font-mono font-medium hover:bg-primary/5 hover:border-primary/30 click-effect" onClick={() => handleKeypadClick("0")} disabled={isLoading} data-testid="key-0">0</Button>
            <Button variant="ghost" className="h-14 text-lg" onClick={handleBackspace} disabled={isLoading} data-testid="key-back">←</Button>
          </div>

          {/* Hidden submit alias for automation. PIN auto-submits on the 4th keypad tap. */}
          <button
            type="button"
            data-testid="login-submit-btn"
            onClick={() => handleSubmit()}
            disabled={pin.length !== 4 || isLoading}
            className="sr-only"
            aria-hidden="true"
            tabIndex={-1}
          >
            Submit PIN
          </button>

          {isLoading && (
            <div className="flex items-center justify-center mb-4">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="ml-2 text-sm text-muted-foreground">Signing in...</span>
            </div>
          )}

          <div className="pt-4 border-t border-border">
            <Button variant="outline" className="w-full h-12 gap-2 text-base" onClick={enterClockMode} data-testid="clock-mode-btn">
              <Clock size={20} weight="bold" /> Clock In / Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ===== CLOCK SCREEN =====
const ClockScreen = ({ onBack }) => {
  const [pin, setPin] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [clockedIn, setClockedIn] = useState(false);
  const [clockData, setClockData] = useState(null);
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [token, setToken] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const formatDate = (d) => d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  const handleKeypadClick = (digit) => {
    if (pin.length < 4) {
      const newPin = pin + digit;
      setPin(newPin);
      if (newPin.length === 4) {
        handlePinSubmit(newPin);
      }
    }
  };

  const handlePinSubmit = async (pinValue) => {
    setIsLoading(true);
    try {
      const device = navigator.userAgent || "Clock Terminal";
      const loginRes = await axios.post(`${API}/auth/login`, { pin: pinValue, device, skip_clock_in: true });
      const { token: tk, user } = loginRes.data;
      setToken(tk);
      setUserName(user.name);
      setUserRole(user.role);
      // KDS terminals are shared kitchen displays — they don't clock in
      // or clock out.  Skip the status fetch and surface a friendly
      // explanation instead of showing meaningless action buttons.
      if (user.role === "kds") {
        setClockedIn(false);
        setClockData(null);
      } else {
        const statusRes = await axios.get(`${API}/timeclock/status`, { headers: { Authorization: `Bearer ${tk}` } });
        setClockedIn(statusRes.data.clocked_in);
        setClockData(statusRes.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid PIN");
      setPin("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClockIn = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/timeclock/clock-in`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.clocked_in) {
        toast.success(res.data.already_open ? `${userName} was already clocked in.` : `${userName} clocked in!`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 400 && userRole === "kds") {
        toast.info("Kitchen Display terminals don't clock in or out.");
      } else {
        toast.error(detail || "Couldn't clock in — please try again.");
      }
    } finally {
      setIsLoading(false);
      onBack();
    }
  };

  const handleClockOut = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/timeclock/clock-out`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.clocked_out) {
        toast.success(`${userName} clocked out! ${res.data.hours_worked}h worked.`);
      } else {
        toast.info("You weren't clocked in.");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (userRole === "kds") {
        toast.info("Kitchen Display terminals don't clock in or out.");
      } else {
        toast.error(detail || "Couldn't clock out — please try again.");
      }
    } finally {
      setIsLoading(false);
      onBack();
    }
  };

  const elapsedMs = clockData?.clock_in ? currentTime.getTime() - new Date(clockData.clock_in).getTime() : 0;
  const elapsedH = Math.floor(elapsedMs / 3600000);
  const elapsedM = Math.floor((elapsedMs % 3600000) / 60000);
  const elapsedS = Math.floor((elapsedMs % 60000) / 1000);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-secondary" data-testid="clock-screen">
      <ThemeToggle />
      <Card className="w-full max-w-sm shadow-xl border-border">
        <CardHeader className="text-center pb-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="absolute left-4 top-4 gap-1 text-muted-foreground" data-testid="clock-back-btn">
            <ArrowLeft size={16} /> Back
          </Button>
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Clock size={32} weight="fill" className="text-white" />
          </div>
          <CardTitle className="font-heading text-2xl tracking-tight">Time Clock</CardTitle>
          <div className="mt-2">
            <p className="text-muted-foreground text-xs">{formatDate(currentTime)}</p>
            <p className="font-mono text-3xl font-light tracking-tight" data-testid="clock-live-time">{formatTime(currentTime)}</p>
          </div>
        </CardHeader>
        <CardContent>
          {!token ? (
            /* PIN ENTRY — auto-submits on 4th digit */
            <>
              <p className="text-sm text-muted-foreground text-center mb-4">Enter your PIN</p>
              <div className="flex justify-center gap-3 mb-5">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={`w-12 h-12 rounded-xl border-2 flex items-center justify-center text-xl font-mono font-bold transition-all duration-200 ${pin.length > i ? "border-blue-600 bg-blue-500/10 text-blue-600 dark:text-blue-400" : "border-border bg-background text-transparent"}`} data-testid={`clock-pin-dot-${i}`}>
                    {pin.length > i ? "●" : "○"}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4" data-testid="clock-keypad">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <Button key={digit} variant="outline" className="h-12 text-lg font-mono" onClick={() => handleKeypadClick(String(digit))} disabled={isLoading} data-testid={`clock-key-${digit}`}>{digit}</Button>
                ))}
                <Button variant="ghost" className="h-12 text-xs text-muted-foreground" onClick={() => setPin("")} disabled={isLoading}>Clear</Button>
                <Button variant="outline" className="h-12 text-lg font-mono" onClick={() => handleKeypadClick("0")} disabled={isLoading} data-testid="clock-key-0">0</Button>
                <Button variant="ghost" className="h-12 text-sm" onClick={() => setPin(pin.slice(0, -1))} disabled={isLoading}>←</Button>
              </div>
              {isLoading && (
                <div className="flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">Verifying...</span>
                </div>
              )}
            </>
          ) : (
            /* CLOCK IN / OUT SCREEN */
            <div className="space-y-4" data-testid="clock-action-panel">
              <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-xl">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <User size={24} className="text-primary" weight="bold" />
                </div>
                <div>
                  <p className="font-heading text-lg font-medium" data-testid="clock-employee-name">{userName}</p>
                </div>
              </div>

              {/* Show elapsed time if already clocked in */}
              {clockedIn && clockData?.clock_in && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4" data-testid="clock-elapsed-section">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="text-emerald-700 dark:text-emerald-300 font-medium text-sm">Currently Clocked In</span>
                  </div>
                  <div className="mt-2 bg-emerald-500/20 rounded-lg p-4 text-center">
                    <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-1">Time Worked</p>
                    <p className="font-mono text-4xl font-medium text-emerald-800 dark:text-emerald-100" data-testid="clock-elapsed">
                      {String(elapsedH).padStart(2, "0")}:{String(elapsedM).padStart(2, "0")}:{String(elapsedS).padStart(2, "0")}
                    </p>
                  </div>
                </div>
              )}

              <div className={clockedIn ? "" : "grid grid-cols-1 gap-3"}>
                {userRole === "kds" ? (
                  <div
                    className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-4 text-center"
                    data-testid="clock-kds-notice"
                  >
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      Kitchen Display terminals don&apos;t clock in or out
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-200/80 mt-1">
                      Just log in from the main screen to open the Kitchen Display.
                    </p>
                  </div>
                ) : clockedIn ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      className="h-16 text-lg gap-2 bg-muted text-muted-foreground hover:bg-muted cursor-not-allowed"
                      disabled
                      title="You're already clocked in"
                      data-testid="clock-in-btn"
                    >
                      <SignIn size={22} weight="bold" /> Clock In
                    </Button>
                    <Button
                      onClick={handleClockOut}
                      className="h-16 text-lg gap-2 bg-red-600 hover:bg-red-700"
                      disabled={isLoading}
                      data-testid="clock-out-btn"
                    >
                      <SignOut size={22} weight="bold" /> Clock Out
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleClockIn}
                    className="h-16 text-lg gap-2 bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                    disabled={isLoading}
                    data-testid="clock-in-btn"
                  >
                    <SignIn size={22} weight="bold" /> Clock In
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
